<template>
  <div>
    <Modal
      :is-visible="showModalInternal"
      :title="modalTitle"
      :keep-body-modal-class="keepBodyModalClass"
      wrapper-class="modal-wishlists"
      @onClose="close"
    >
      <template #body>
        <div class="container-fluid py-md-3">
          <div class="row">
            <div class="col-12 p-0">
              <div class="form-group">
                <label for="name">Name</label>
                <input
                  id="name"
                  v-model="nameInternal"
                  maxlength="50"
                  type="text"
                  class="form-control"
                  placeholder="Marketing, Analytics, Software, etc."
                >
              </div>

              <div class="form-group">
                <label for="description">Description</label>
                <span class="helptext">(optional)</span>
                <textarea
                  id="description"
                  v-model="descriptionInternal"
                  class="form-control text-dark"
                  placeholder="Include a short description of your list."
                  rows="5"
                />
              </div>
              <CharacterCount
                :length="descriptionInternal.length"
                :maxlength="200"
              />

              <!-- TODO: Use dorado toggle component -->
              <div class="form-check">
                <input
                  id="is-private"
                  v-model="isPrivateInternal"
                  type="checkbox"
                  class="form-check-input"
                >
                <label class="form-check-label" for="is-private">Make this wishlist private?</label>
              </div>

              <p class="wishlist-privacy-text mt-2">
                If you don’t want others to see this wishlist, make it private.<br>
                <a
                  href="https://appsumo.com/privacy/"
                  rel="noopener"
                  target="_blank"
                  class="privacy-policy"
                >
                  See our privacy policy
                </a>
              </p>
              <!-- Delete Wishlist -->
              <p
                v-if="isEditing"
                class="mt-2 wishlist-delete-text"
                @click="beginDeleteList"
              >
                Delete this wishlist
              </p>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          type="button"
          class="btn btn-outline-secondary mr-3"
          @click.prevent="close"
        >
          Cancel
        </button>
        <button
          v-if="isEditing"
          type="button"
          class="btn btn-secondary"
          :disabled="!formIsValid || isProcessing"
          @click.prevent="editList"
        >
          Save
        </button>
        <button
          v-else-if="isDuplicating"
          type="button"
          class="btn btn-secondary"
          :disabled="!formIsValid || isProcessing"
          @click.prevent="duplicateList"
        >
          Duplicate
        </button>
        <button
          v-else
          type="button"
          class="btn btn-primary"
          :disabled="!formIsValid || isProcessing"
          @click.prevent="createList"
        >
          Create
        </button>
      </template>
    </Modal>
    <DeleteModal
      :show-modal="showDeleteModal"
      :list-name="name"
      @confirm="deleteWishlist"
      @close="updateShowDeleteModal"
    />
  </div>
</template>

<script>
import get from 'lodash/get';
import DeleteModal from './DeleteModal.vue';
import Modal from '../common/Modal.vue';
import CharacterCount from '../common/CharacterCount.vue';

export default {
  components: {
    DeleteModal,
    Modal,
    CharacterCount,
  },

  props: {
    showModal: {
      type: Boolean,
      required: true,
      default: false,
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDuplicating: {
      type: Boolean,
      required: false,
      default: false,
    },
    wishlistId: {
      type: Number,
      required: false,
      default: null,
    },
    // Input props
    name: {
      type: String,
      required: false,
      default: '',
    },
    description: {
      type: String,
      required: false,
      default: '',
    },
    isPrivate: {
      type: Boolean,
      required: false,
      default: false,
    },
    keepBodyModalClass: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // For internal use, this should be sync with the showModal property
      showModalInternal: this.showModal,
      showDeleteModal: false,
      nameInternal: this.name,
      descriptionInternal: this.description,
      isPrivateInternal: this.isPrivate,
    };
  },

  computed: {
    modalTitle() {
      if (this.isEditing) {
        return 'Edit wishlist';
      }

      if (this.isDuplicating) {
        return 'Duplicate wishlist';
      }

      return 'Create wishlist';
    },

    userId() {
      return get(this, '$store.state.session.user.id');
    },

    requestErrorMessage() {
      return this.$store.getters['wishlists/errorMessage'];
    },

    isProcessing() {
      return this.$store.getters['wishlists/isProcessing'];
    },

    formIsValid() {
      return this.nameInternal?.length <= 50 && this.descriptionInternal.length <= 200;
    },
  },

  watch: {
    showModal(newValue) {
      // Sync internal variable
      this.showModalInternal = newValue;
    },
  },

  methods: {
    clean() {
      if (!this.isEditing && !this.isDuplicating) {
        this.nameInternal = '';
        this.descriptionInternal = '';
        this.isPrivateInternal = false;
      }
    },

    close() {
      this.showModalInternal = false;
      this.clean();
      this.$emit('update:showModal', this.showModalInternal);
      this.$emit('onClose', this.showModalInternal);
    },

    getRequestData() {
      return {
        user: this.userId,
        name: this.nameInternal,
        description: this.descriptionInternal,
        is_public: !this.isPrivateInternal,
        is_active: true,
        originalWishlistId: this.wishlistId,
      };
    },

    beginDeleteList() {
      this.close();
      // add wait here?
      this.updateShowDeleteModal(true);
    },

    updateShowDeleteModal(show = false) {
      // if we are showing delete, don't show the details modal
      this.showDeleteModal = show;
      this.showModalInternal = !show;
    },

    async wishlistStore(action, onComplete, dataOverride = null) {
      if (this.isProcessing) {
        return;
      }

      const requestData = dataOverride || this.getRequestData();

      await this.$store.dispatch(action, requestData);

      if (this.requestErrorMessage) {
        this.$toastr.e(this.requestErrorMessage);
        return;
      }

      // call unique complete function
      onComplete();
    },

    onCreateComplete() {
      this.$toastr.s(`Wishlist <strong>${this.nameInternal}</strong> created!`);
      this.clean();
      this.$emit('wishlistAdded');
    },

    async createList() {
      await this.wishlistStore('wishlists/createWishlist', this.onCreateComplete);
    },

    onEditComplete() {
      this.$toastr.s(`Wishlist <strong>${this.nameInternal}</strong> updated!`);
      this.close();
      this.$emit('wishlistUpdated');
    },

    async editList() {
      await this.wishlistStore('wishlists/updateWishlist', this.onEditComplete);
    },

    onDuplicateComplete() {
      const newSlug = this.$store.getters['wishlists/nextSlug'];

      this.$toastr.s(`Wishlist <strong>${this.nameInternal}</strong> duplicated!`);
      this.$emit('wishlistDuplicated');

      // redirect to the new collection
      window.location = `/collections/wishlists/${newSlug}/`;
    },

    async duplicateList() {
      await this.wishlistStore('wishlists/duplicateWishlist', this.onDuplicateComplete);
    },

    onDeleteComplete() {
      this.$toastr.s(`Wishlist <strong>${this.nameInternal}</strong> was deleted!`);
      this.$emit('wishlistDeleted');

      window.location = '/account/wishlists/';
    },

    async deleteWishlist() {
      const reqData = { wishlistId: this.wishlistId };
      await this.wishlistStore('wishlists/deleteWishlist', this.onDeleteComplete, reqData);
    },
  },
};
</script>

<style lang="scss" scoped>
// TODO: Remove this when dorado is implemented
@import '~/styles/dorado/main';
@import '~/styles/utils/breakpoints';

/*! purgecss ignore */
div ::v-deep {
  .modal-dialog-scrollable {
    max-height: calc(100% - 7rem);
  }

  .modal-wishlists .modal-dialog {
    left: 0;
    margin-top: 7rem;
    position: relative;
    top: 0;
  }

  .modal-body {
    box-shadow: none;
  }
}

textarea {
  resize: none;
}

.btn {
  border-radius: 40px;
  padding: 8px 24px;
}

.wishlist-privacy-text {
  font-size: 14px;
  color: $grace;

  .privacy-policy {
    color: $grace;
    text-decoration: underline;
  }
}

.wishlist-delete-text {
  color: $danger;
  text-decoration: underline;
  cursor: pointer;
}

.helptext {
  color: $grace;
  margin-left: 5px;
}

@include media-breakpoint-down(sm) {
  /*! purgecss ignore */
  div ::v-deep {
    .modal-dialog-scrollable {
      max-height: calc(100% - 2rem);
    }

    .modal-wishlists .modal-dialog {
      margin-top: 1rem;
    }

    .modal-header {
      padding-bottom: 0.5rem;
    }
  }
}
</style>
